/**
 * SendQueueSheet (FEAT-037) — the answer to "did it send?".
 *
 * Until now the only thing the app said about a moment on its way out was a
 * pill with a count on it. If a clip did not arrive there was nowhere to look:
 * no list, no reason, no handle. This is that list — one card per moment, the
 * place it is going, what is happening to it in plain words, and, when it needs
 * a person, Try again or Discard.
 *
 * Three rules it keeps:
 *
 * - **Every number is the server's.** An uploading row shows the percentage of
 *   bytes the server has CONFIRMED, never an estimate the client invented
 *   (BUG-058). A row with nothing on the wire says so instead of spinning.
 * - **Discard is not delete.** Giving up on a send keeps the moment: the row
 *   goes and the capture becomes an unsent draft again, his to send elsewhere
 *   or throw away deliberately. Armed-then-confirmed, like StuckQueueCard.
 * - **Opaque, not glass.** This opens over the camera band, where a translucent
 *   surface is unreadable — the same call the drafts sheet and StuckQueueCard
 *   already made. Inline markup rather than `molecules/Sheet` for the same
 *   reason: camera-world screens keep their own.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { sendEngine } from '../services/queue/engine';
import type { QueueRow } from '../services/queue/types';
import { mediaStore } from '../services/media/mediaStore';
import { destinationName, rowSentence } from '../utils/sendQueueCopy';
import { useFriendsStore } from '../store/friendsStore';
import { useGroupStore } from '../store/groupStore';
// Direct import, not the hooks barrel: the barrel pulls in useWebSocket and
// the whole messageStore behind it, which this sheet has no business loading.
import { useOnline } from '../hooks/useOnline';
import { MediaPoster } from '../atoms';

interface SendQueueSheetProps {
  open: boolean;
  onClose: () => void;
}

/** What a card knows about the moment behind its rows. */
interface MomentCard {
  mediaId: string;
  mediaType: 'photo' | 'video';
  poster?: { bytes: ArrayBuffer; type: string };
  rows: QueueRow[];
}

export default function SendQueueSheet({ open, onClose }: SendQueueSheetProps) {
  const [cards, setCards] = useState<MomentCard[]>([]);
  const [armedKey, setArmedKey] = useState<string | null>(null);
  const friends = useFriendsStore((s) => s.friends);
  const groups = useGroupStore((s) => s.groups);
  // Live, not a sample at render: the sheet stays open across a reconnect,
  // and its rows must flip from "Waiting for signal" the moment it happens.
  const online = useOnline();
  const offline = !online;

  const friendName = useCallback(
    (id: number) => {
      const found = friends.find((f) => f.friend.id === id);
      return found?.nickname || found?.friend.display_name || found?.friend.username;
    },
    [friends]
  );
  const groupName = useCallback(
    (id: number) => groups.find((g) => g.id === id)?.name,
    [groups]
  );

  // Rows come from the ledger's in-memory mirror, so this costs nothing and is
  // never stale. The posters are ONE small meta read per moment in the queue —
  // never a walk of the store, and never anything near the chunks.
  useEffect(() => {
    if (!open) {
      setCards([]);
      return;
    }
    let alive = true;
    const rebuild = async () => {
      const byMedia = new Map<string, QueueRow[]>();
      for (const row of sendEngine.rows()) {
        const list = byMedia.get(row.mediaId);
        if (list) list.push(row);
        else byMedia.set(row.mediaId, [row]);
      }
      const next: MomentCard[] = [];
      for (const [mediaId, rows] of byMedia) {
        rows.sort((a, b) => a.createdAt - b.createdAt);
        const meta = await mediaStore.meta(mediaId).catch(() => null);
        next.push({
          mediaId,
          mediaType: rows[0].mediaType,
          poster: meta?.thumbnail,
          rows,
        });
      }
      next.sort((a, b) => a.rows[0].createdAt - b.rows[0].createdAt);
      if (alive) setCards(next);
    };
    const unsubRows = sendEngine.onChange(() => void rebuild());
    // Progress is its own signal: an offset lands every couple of megabytes,
    // and it must not drag a metadata walk along behind it.
    const unsubProgress = sendEngine.onProgress(() => {
      if (alive) setCards((current) => [...current]);
    });
    return () => {
      alive = false;
      unsubRows();
      unsubProgress();
    };
  }, [open]);

  const handleDiscard = useCallback(
    (clientKey: string) => {
      if (armedKey !== clientKey) {
        setArmedKey(clientKey);
        setTimeout(() => setArmedKey((k) => (k === clientKey ? null : k)), 3000);
        return;
      }
      setArmedKey(null);
      void sendEngine.discardKeys([clientKey]);
    },
    [armedKey]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-md rounded-t-2xl bg-surface p-4 pb-safe animate-slide-up pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Moments on their way"
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-text-muted/30" />
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text">On their way</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:scale-95"
          >
            <X size={20} />
          </button>
        </div>

        {/* The first-tap explainer Nation asked for — always there offline,
            because the question it answers is only asked offline. */}
        {offline && (
          <p className="-mt-2 mb-3 text-center text-sm text-text-muted">
            These will send once you're back online.
          </p>
        )}

        {cards.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-muted">
            Everything has landed.
          </p>
        ) : (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto">
            {cards.map((card) => {
              const progress = sendEngine.mediaProgress(card.mediaId);
              const percent =
                progress && progress.total > 0
                  ? Math.min(100, Math.round((progress.sent / progress.total) * 100))
                  : null;
              return (
                <div
                  key={card.mediaId}
                  className="flex gap-3 rounded-xl bg-background p-2"
                >
                  <MediaPoster
                    poster={card.poster}
                    mediaType={card.mediaType}
                    className="h-20 w-16 shrink-0 rounded-lg"
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    {card.rows.map((row) => (
                      <div key={row.clientKey}>
                        <p className="flex items-center gap-1.5 truncate text-sm font-medium text-text">
                          {row.state === 'stuck' && (
                            <AlertTriangle size={14} className="shrink-0 text-warning" />
                          )}
                          {rowSentence(
                            row,
                            destinationName(row.dest, friendName, groupName),
                            offline,
                            percent
                          )}
                        </p>
                        {row.state === 'stuck' && row.stuckReason && (
                          <p className="truncate text-xs text-text-muted">
                            {row.stuckReason}
                          </p>
                        )}
                        <div className="mt-1.5 flex gap-2">
                          <button
                            onClick={() => void sendEngine.retryKeys([row.clientKey])}
                            className="min-h-[44px] flex-1 rounded-xl bg-primary text-sm font-semibold text-on-primary transition-transform active:scale-95"
                          >
                            Try again
                          </button>
                          <button
                            onClick={() => handleDiscard(row.clientKey)}
                            className={`min-h-[44px] flex-1 rounded-xl border text-sm font-semibold transition-transform active:scale-95 ${
                              armedKey === row.clientKey
                                ? 'border-error bg-error text-on-primary'
                                : 'border-divider text-error'
                            }`}
                          >
                            {armedKey === row.clientKey ? 'Tap to confirm' : 'Discard'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="pt-3 text-center text-xs text-text-muted">
          Discarding keeps the moment — it goes back to your drafts.
        </p>
      </div>
    </div>
  );
}
